import { HttpAgent } from "@icp-sdk/core/agent";
import {
	Delegation,
	DelegationChain,
	DelegationIdentity,
	Ed25519KeyIdentity,
} from "@icp-sdk/core/identity";
import { OdinSiwbClient } from "@/lib/clients/OdinSiwbClient";
import {
	connectBitcoinWallet,
	WalletFactory,
} from "@/lib/services/btc-wallet-providers";
import {
	buildSiwbSignMessageType,
	normalizeBitcoinPublicKey,
	signBitcoinMessageWithFallback,
} from "@/lib/services/btc-signing-profile";
import { OdinFunAuthService } from "@/lib/services/odin-fun-auth";
import {
	getMobileWalletEnvironment,
	handleMobileWalletConnection,
} from "@/lib/utils/mobile-detection";
import { SessionKeyEncryption } from "@/lib/utils/session-key-encryption";
import { createLogAdapter } from "@/lib/logger/console-adapter";

export interface RefreshAuthOptions {
	showMinimalUI?: boolean;
	forceFullRefresh?: boolean;
	onProgress?: (status: string) => void;
	onError?: (error: string) => void;
}

export interface RefreshAuthResult {
	success: boolean;
	principal?: string;
	address?: string;
	identity?: DelegationIdentity;
	error?: string;
	needsFullLogin?: boolean;
}

const uiLog = createLogAdapter("lib/services/siwb-auth-refresh.ts");

export class SiwbAuthRefreshService {
	/**
	 * 检查当前身份是否过期
	 */
	static isIdentityExpired(): boolean {
		try {
			const delegationResult = localStorage.getItem("user_delegation_result");
			if (!delegationResult) return true;

			const parsedChain = JSON.parse(delegationResult);
			const expiration = BigInt(
				parsedChain.delegationData.delegation.expiration,
			);
			const now = BigInt(Date.now() * 1000000); // 转换为纳秒

			return now >= expiration;
		} catch (error) {
			uiLog.error("检查身份过期状态失败:", error);
			return true;
		}
	}

	/**
	 * 获取当前存储的钱包信息
	 */
	static getCurrentWalletInfo(): {
		walletType: string;
		address: string;
	} | null {
		const walletType = localStorage.getItem("user_wallet_type");
		const address = localStorage.getItem("user_btc_address");

		if (!walletType || !address) {
			return null;
		}

		return { walletType, address };
	}

	/**
	 * 刷新授权
	 */
	static async refreshAuth(
		options: RefreshAuthOptions = {},
	): Promise<RefreshAuthResult> {
		const {
			showMinimalUI = true,
			forceFullRefresh = false,
			onProgress,
			onError,
		} = options;

		try {
			// 1. 检查是否需要完整重新登录
			const walletInfo = SiwbAuthRefreshService.getCurrentWalletInfo();
			if (!walletInfo) {
				return {
					success: false,
					error: "需要重新完整登录",
					needsFullLogin: true,
				};
			}

			const { walletType, address } = walletInfo;

			// 2. 强制执行完整的 SIWB 重新登录流程
			// 注意：更新授权时我们总是需要重新获取 SIWB 委托，即使当前委托未过期
			uiLog.info("🔄 强制执行完整的 SIWB 重新登录以获取最新授权");

			// 如果只是想刷新 odin.fun 授权而不重新获取 SIWB 委托，可以取消下面的注释
			// const isExpired = this.isIdentityExpired();
			// if (!isExpired && !forceFullRefresh) {
			//   // 只刷新 odin.fun 授权
			//   return await this.refreshOdinFunAuthOnly();
			// }

			onProgress?.("正在检测钱包连接...");

			// 3. 检查钱包是否可用
			const wallet = WalletFactory.getWallet(walletType);
			if (!wallet) {
				throw new Error(`钱包 ${walletType} 不可用`);
			}

			// 4. 移动端特殊处理
			const mobileEnv = getMobileWalletEnvironment();
			if (mobileEnv.isMobile && mobileEnv.walletType !== walletType) {
				const canConnect = wallet.isAvailable();
				if (!canConnect) {
					// 尝试深链接跳转
					const walletNames = {
						okx: "OKX钱包",
						unisat: "UniSat钱包",
						xverse: "Xverse钱包",
						phantom: "Phantom钱包",
						tokenpocket: "TokenPocket钱包",
					};
					const walletName =
						walletNames[walletType as keyof typeof walletNames] || walletType;

					onProgress?.(`正在尝试打开${walletName}...`);

					const success = await handleMobileWalletConnection(
						walletType,
						() => {
							onProgress?.(`请在${walletName}中完成签名后返回`);
						},
						(errorMessage) => {
							onError?.(errorMessage);
						},
					);

					if (!success) {
						return {
							success: false,
							error: `无法连接到${walletName}，请在钱包中打开此页面`,
							needsFullLogin: true,
						};
					}
				}
			}

			// 5. 验证钱包连接
			if (!wallet.isAvailable()) {
				throw new Error(`${walletType}钱包未连接，请先连接钱包`);
			}

			onProgress?.("正在验证钱包连接...");

			// 6. 直接跳过地址验证，使用存储的地址进行签名
			// 注意：我们将在签名步骤中让钱包进行实际连接
			uiLog.info("📍 使用存储的地址进行授权刷新:", address);

			// 7. 执行 SIWB 刷新流程
			return await SiwbAuthRefreshService.executeSiwbRefresh(
				walletType,
				address,
				onProgress,
			);
		} catch (error: any) {
			uiLog.error("授权刷新失败:", error);
			const errorMessage = error.message || "授权刷新失败，请重试";
			onError?.(errorMessage);

			return {
				success: false,
				error: errorMessage,
				needsFullLogin:
					error.message?.includes("重新完整登录") ||
					error.message?.includes("地址已更改"),
			};
		}
	}

	/**
	 * 只刷新 odin.fun 授权（身份未过期时）
	 */
	private static async refreshOdinFunAuthOnly(): Promise<RefreshAuthResult> {
		try {
			// 重建当前身份
			const identity = await SiwbAuthRefreshService.rebuildCurrentIdentity();
			if (!identity) {
				throw new Error("无法重建当前身份");
			}

			// 刷新 odin.fun 授权
			await OdinFunAuthService.getOdinFunAuth(identity);

			return {
				success: true,
				principal: identity.getPrincipal().toString(),
				address: localStorage.getItem("user_btc_address") || "",
				identity,
			};
		} catch (error: any) {
			return {
				success: false,
				error: error.message || "odin.fun 授权刷新失败",
			};
		}
	}

	/**
	 * 重建当前身份（从 localStorage）
	 */
	private static async rebuildCurrentIdentity(): Promise<DelegationIdentity | null> {
		try {
			const storedDelegationResult = localStorage.getItem(
				"user_delegation_result",
			);
			const storedPrincipal = localStorage.getItem("user_principal");

			if (!storedDelegationResult || !storedPrincipal) {
				return null;
			}

			// 尝试迁移旧的未加密密钥
			await SessionKeyEncryption.migrateUnencryptedKey(storedPrincipal);

			// 解密会话私钥
			const decryptedPrivateKey =
				await SessionKeyEncryption.decryptSessionKey(storedPrincipal);
			if (!decryptedPrivateKey) {
				return null;
			}

			// 重建会话身份
			const sessionPrivateKeyBuffer = new Uint8Array(
				Buffer.from(decryptedPrivateKey, "hex"),
			);
			const sessionIdentity =
				Ed25519KeyIdentity.fromSecretKey(sessionPrivateKeyBuffer);

			// 解析委托数据
			const parsedChain = JSON.parse(storedDelegationResult, (key, value) => {
				if (key === "expiration" && typeof value === "string") {
					return BigInt(value);
				}
				return value;
			});

			// 重建委托链
			const userCanisterPubkeyDer = new Uint8Array(
				Object.values(parsedChain.userCanisterPubkey),
			);
			const delegationPubkey = new Uint8Array(
				Object.values(parsedChain.delegationData.delegation.pubkey),
			);
			const signature = new Uint8Array(
				Object.values(parsedChain.delegationData.signature),
			);

			const delegation = new Delegation(
				delegationPubkey,
				BigInt(parsedChain.delegationData.delegation.expiration),
				parsedChain.delegationData.delegation.targets
					? parsedChain.delegationData.delegation.targets[0]
					: undefined,
			);

			const chain = DelegationChain.fromDelegations(
				[
					{
						delegation: delegation,
					signature: signature as any,
					},
				],
				userCanisterPubkeyDer,
			);

			// 创建最终身份
			const identity = DelegationIdentity.fromDelegation(
				sessionIdentity,
				chain,
			);

			return identity;
		} catch (error) {
			uiLog.error("重建身份失败:", error);
			return null;
		}
	}

	/**
	 * 执行 SIWB 刷新流程 - 完整的重新登录过程
	 * 1. siwb_prepare_login - 获取签名消息
	 * 2. 钱包签名 - 用户确认签名
	 * 3. siwb_login - 验证签名并登录
	 * 4. siwb_get_delegation - 获取新的委托授权
	 * 5. 保存新的身份和授权数据
	 */
	private static async executeSiwbRefresh(
		walletType: string,
		address: string,
		onProgress?: (status: string) => void,
	): Promise<RefreshAuthResult> {
		try {
			uiLog.info("🚀 开始执行完整的 SIWB 重新登录流程");
			uiLog.info("📱 钱包类型:", walletType);
			uiLog.info("📧 地址:", address);

			// 创建 HttpAgent
			const agent = new HttpAgent({
				host: process.env.NEXT_PUBLIC_IC_HOST || "https://ic0.app",
			});

			// 在开发环境中获取根密钥
			if (process.env.NODE_ENV === "development") {
				await agent.fetchRootKey().catch((error) => {
					uiLog.error("fetchRootKey failed:", error);
				});
			}

			const siwbClient = new OdinSiwbClient(agent);

			// 生成会话密钥对
			const sessionIdentity = Ed25519KeyIdentity.generate();
			const sessionKey = new Uint8Array(sessionIdentity.getPublicKey().toDer());

			onProgress?.("正在准备签名消息...");

			// 步骤1: 获取待签名消息 (siwb_prepare_login)
			uiLog.debug("📝 步骤1: 调用 siwb_prepare_login 获取签名消息");
			const prepareResponse = await siwbClient.siwb_prepare_login(address);
			if ("Err" in prepareResponse) {
				throw new Error(`准备登录失败: ${prepareResponse.Err}`);
			}

			const messageToSign = prepareResponse.Ok;
			uiLog.debug("✅ 步骤1完成: 获取到签名消息:", messageToSign);

			onProgress?.("请在钱包中确认签名...");

			// 步骤2: 钱包签名
			uiLog.debug("🔐 步骤2: 开始钱包签名");
			const wallet = WalletFactory.getWallet(walletType);
			if (!wallet) {
				throw new Error(`钱包 ${walletType} 不可用`);
			}

			// 确保钱包已连接
			if (!wallet.isAvailable()) {
				throw new Error(`${walletType}钱包未连接，请先连接钱包`);
			}

			uiLog.debug("🔐 调用钱包签名，消息:", messageToSign);
			uiLog.debug("📧 签名地址:", address);
			const {
				signature,
				protocol: usedProtocol,
				addressType,
			} = await signBitcoinMessageWithFallback({
				wallet,
				walletType,
				address,
				message: messageToSign,
				log: (level, message, data) => {
					uiLog[level](message, data);
				},
			});
			uiLog.debug("✅ 步骤2完成: 钱包签名完成，签名:", signature);
			uiLog.debug("🧭 已解析签名协议:", {
				walletType,
				addressType,
				usedProtocol,
			});

			// 获取公钥（复用原有逻辑）
			let actualPublicKey: string | undefined;
			if (walletType === "okx") {
				if (wallet.getPublicKey) {
					actualPublicKey = await wallet.getPublicKey();
				}
				if (!actualPublicKey) {
					const storedPublicKey =
						typeof window !== "undefined"
							? localStorage.getItem("user_btc_publicKey") || undefined
							: undefined;
					actualPublicKey = storedPublicKey;
				}
				if (!actualPublicKey) {
					try {
						const connectionResult = await connectBitcoinWallet(walletType);
						actualPublicKey = connectionResult.publicKey;
					} catch (error) {
						uiLog.warn("重新连接 OKX 获取 connect publicKey 失败:", error);
					}
				}
				if (!actualPublicKey) {
					throw new Error("OKX钱包未提供公钥读取能力");
				}
			} else if (walletType === "xverse") {
				const xverseWallet = wallet as any;
				if (xverseWallet.getPublicKey) {
					actualPublicKey = await xverseWallet.getPublicKey();
				}
				actualPublicKey = normalizeBitcoinPublicKey(address, actualPublicKey);
			} else {
				// 从存储的连接信息获取公钥
				try {
					const connectionResult = await connectBitcoinWallet(walletType);
					actualPublicKey = normalizeBitcoinPublicKey(
						address,
						connectionResult.publicKey,
					);
				} catch (error) {
					// 如果连接失败，使用已存储的钱包类型和地址继续
					uiLog.warn("获取公钥失败，继续使用现有地址:", error);
				}
			}

			onProgress?.("正在验证签名...");

			// 步骤3: 准备 siwb_login 参数
			uiLog.debug("⚙️ 步骤3准备: 确定签名验证方式和公钥");

			const signMessageType = buildSiwbSignMessageType(usedProtocol);

			// 步骤3: 执行 siwb_login 验证签名并登录
			uiLog.debug("🔍 步骤3: 调用 siwb_login 验证签名并登录");
			uiLog.debug("📝 siwb_login 参数:", {
				signature: signature.substring(0, 20) + "...",
				address,
				actualPublicKey: actualPublicKey?.substring(0, 20) + "...",
				sessionKeyLength: sessionKey.length,
				signMessageType,
			});

			const loginResponse = await siwbClient.siwb_login(
				signature,
				address,
				actualPublicKey ?? "",
				sessionKey,
				signMessageType,
			);

			if ("Err" in loginResponse) {
				throw new Error(`登录失败: ${loginResponse.Err}`);
			}

			const loginDetails = loginResponse.Ok;
			uiLog.info("✅ 步骤3完成: siwb_login 成功，获得登录详情");

			onProgress?.("正在获取授权委托...");

			// 步骤4: 获取新的委托授权 (siwb_get_delegation)
			uiLog.debug("🎫 步骤4: 调用 siwb_get_delegation 获取新的委托授权");
			uiLog.debug("📝 siwb_get_delegation 参数:", {
				address,
				sessionKeyLength: sessionKey.length,
				expiration: loginDetails.expiration.toString(),
			});

			const delegationResponse = await siwbClient.siwb_get_delegation(
				address,
				sessionKey,
				loginDetails.expiration,
			);

			if ("Err" in delegationResponse) {
				throw new Error(`获取委托失败: ${delegationResponse.Err}`);
			}

			// 创建最终身份
			const delegationResult = delegationResponse.Ok;
			uiLog.info("✅ 步骤4完成: 获取到新的委托授权");
			uiLog.debug(
				"⏰ 新委托的过期时间:",
				delegationResult.delegation.expiration.toString(),
			);

			const userCanisterPubkeyDer = new Uint8Array(
				loginDetails.user_canister_pubkey,
			);

			// 步骤5: 创建新的身份和保存授权数据
			uiLog.debug("🔧 步骤5: 创建新身份并保存授权数据");

			const delegationChain =
				SiwbAuthRefreshService.createDelegationChainFromGetResult(
					delegationResult,
					userCanisterPubkeyDer,
				);
			const finalIdentity = DelegationIdentity.fromDelegation(
				sessionIdentity,
				delegationChain,
			);

			const principal = finalIdentity.getPrincipal().toString();
			uiLog.info("🆔 新身份 Principal:", principal);

			// 保存到本地存储 - 覆盖旧的授权数据
			uiLog.debug("💾 保存新的授权数据到本地存储");
			localStorage.setItem("siwb_authenticated", "true");
			localStorage.setItem("user_btc_address", address);
			localStorage.setItem("user_principal", principal);
			localStorage.setItem("user_wallet_type", walletType);
			localStorage.setItem(
				"user_session_key",
				Buffer.from(sessionKey).toString("hex"),
			);
			// 使用加密存储会话私钥
			await SessionKeyEncryption.encryptSessionKey(
				Buffer.from(sessionIdentity.getKeyPair().secretKey).toString("hex"),
				principal,
			);

			const delegationWithMeta = {
				delegationData: delegationResult,
				userCanisterPubkey: Array.from(userCanisterPubkeyDer),
			};

			localStorage.setItem(
				"user_delegation_result",
				JSON.stringify(delegationWithMeta, (key, value) =>
					typeof value === "bigint" ? value.toString() : value,
				),
			);

			uiLog.info("✅ 步骤5完成: 新的授权数据已保存");

			onProgress?.("正在更新平台授权...");

			// 步骤6: 获取 odin.fun 授权
			uiLog.info("🌐 步骤6: 更新 odin.fun 平台授权");
			try {
				await OdinFunAuthService.getOdinFunAuth(finalIdentity);
				uiLog.info("✅ 步骤6完成: odin.fun 授权更新成功");
			} catch (odinFunError: any) {
				uiLog.warn("⚠️ 步骤6警告: odin.fun 授权失败:", odinFunError);
				// odin.fun 授权失败不阻止整个流程
			}

			uiLog.info("🎉 完整的 SIWB 重新登录流程完成！");
			uiLog.debug("📊 最终结果:", {
				principal,
				address,
				walletType,
				identityType: finalIdentity.constructor.name,
			});

			return {
				success: true,
				principal,
				address,
				identity: finalIdentity,
			};
		} catch (error: any) {
			throw error;
		}
	}

	/**
	 * 根据 get_delegation 结果创建委托链
	 */
	private static createDelegationChainFromGetResult(
		getResult: any,
		middlePubkeyDer: Uint8Array,
	) {
		const delegations = [
			{
				delegation: new Delegation(
					getResult.delegation.pubkey,
					getResult.delegation.expiration,
					getResult.delegation.targets
						? getResult.delegation.targets[0]
						: undefined,
				),
				signature: getResult.signature,
			},
		];
		return DelegationChain.fromDelegations(delegations, middlePubkeyDer);
	}
}
